/**
 * A simplified version of TimeSeriesGraph for Storybook
 * Updated to use Striim Design System
 */

import dateLib from "core/utils/date-utils";
import React, { JSXElementConstructor, ReactElement } from "react";
import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import { StriimTypography } from "@striim/striim-ui-v2";
import { Box } from "@mui/material";
import typography from "src/theme/typography";

interface SimpleTimeSeriesProps {
    data: any[];
    series: {
        dataKey: string;
        name: string;
        color: string;
        dot?: ReactElement<SVGElement, string | JSXElementConstructor<any>>;
    }[];
    width?: number;
    height?: number;
    title?: string;
}

// Tooltip styles following design system
const tooltipStyles = {
    contentStyle: {
        fontFamily: typography.fontFamily,
        fontSize: "12px",
        fontWeight: 400,
        borderRadius: 4,
        border: "1px solid #D8E4F0",
        backgroundColor: "#fff",
        padding: "8px"
    },
    labelStyle: {
        fontFamily: typography.fontFamily,
        fontSize: "14px",
        fontWeight: 500,
        color: "#0D1417"
    },
    itemStyle: {
        fontFamily: typography.fontFamily,
        fontSize: "12px",
        fontWeight: 400,
        color: "#4E5C62"
    }
};

const SimpleTimeSeriesGraph: React.FC<SimpleTimeSeriesProps> = ({ data, series, width = 800, height = 400, title }) => {
    return (
        <Box sx={{ width: width, height: height, margin: "auto" }}>
            {title && (
                <StriimTypography
                    variant="h3"
                    color="primary.700"
                    sx={{
                        textAlign: "center",
                        marginBottom: "16px",
                        fontFamily: typography.fontFamily,
                        fontSize: "16px",
                        fontWeight: 600,
                        lineHeight: "24px"
                    }}
                    data-test-id="time-series-graph-title"
                >
                    {title}
                </StriimTypography>
            )}

            <LineChart
                width={width}
                height={height - (title ? 50 : 0)}
                data={data}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
                <CartesianGrid strokeDasharray="3 3" stroke="#D8E4F0" />
                <XAxis
                    dataKey="timestamp"
                    tickFormatter={function(val) {
                        return dateLib(val).format(" D-M-YY h:mm:ss a  ");
                    }}
                    tickLine={false}
                    axisLine={{ stroke: "#D8E4F0" }}
                    tick={{
                        fontFamily: typography.fontFamily,
                        fontSize: 12,
                        fill: "#6E7D8C"
                    }}
                />
                <YAxis
                    tickLine={false}
                    axisLine={{ stroke: "#D8E4F0" }}
                    tick={{
                        fontFamily: typography.fontFamily,
                        fontSize: 12,
                        fill: "#6E7D8C"
                    }}
                />
                <Tooltip
                    labelFormatter={value => {
                        return dateLib(value).format("D-M-YY h:mm:ss a");
                    }}
                    contentStyle={tooltipStyles.contentStyle}
                    labelStyle={tooltipStyles.labelStyle}
                    itemStyle={tooltipStyles.itemStyle}
                />
                <Legend
                    wrapperStyle={{
                        fontFamily: typography.fontFamily,
                        fontSize: "14px"
                    }}
                    iconType="line"
                />

                {series.map(s => (
                    <Line
                        key={s.dataKey}
                        type="monotone"
                        dataKey={s.dataKey}
                        name={s.name}
                        stroke={s.color}
                        activeDot={{ r: 4 }}
                        isAnimationActive={false}
                        dot={s.dot ? s.dot : undefined}
                        strokeWidth={2}
                    />
                ))}
            </LineChart>
        </Box>
    );
};

export default SimpleTimeSeriesGraph;
